import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Loader2, CreditCard } from "lucide-react";
import PaymentMethodChip from "./PaymentMethodChip";

/**
 * Admin Reports — Payment Method statistics card.
 * Lists every payment method that has at least one booking with:
 *   - total bookings, paid count, unpaid count
 *   - paid revenue, unpaid outstanding amount
 * Plus a totals strip.
 */
export default function PaymentMethodReports() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ methods: [], totals: {} });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/payment-method-stats")
      .then(({ data: d }) => { if (!cancelled) setData(d || { methods: [], totals: {} }); })
      .catch(() => { if (!cancelled) setData({ methods: [], totals: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-white rounded-3xl border border-gray-200 p-5 sm:p-6 shadow-sm" data-testid="payment-method-reports">
      <div className="flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-[#047857]" />
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-700">Payment Method Reports</div>
      </div>
      <h3 className="mt-1 text-xl font-extrabold tracking-tight font-[Outfit] text-gray-900">How customers are paying you</h3>

      {/* Totals strip */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="payment-totals">
        <Stat label="Total bookings" value={data.totals?.bookings || 0} />
        <Stat label="Paid bookings" value={data.totals?.paid_bookings || 0} />
        <Stat label="Paid revenue" value={`$${(data.totals?.paid_revenue || 0).toFixed(2)}`} accent />
        <Stat label="Outstanding" value={`$${(data.totals?.unpaid_amount || 0).toFixed(2)}`} />
      </div>

      {/* Per-method breakdown */}
      <div className="mt-5 space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Loading payment stats…</div>
        ) : data.methods.length === 0 ? (
          <div className="py-6 text-sm text-gray-500 text-center">No bookings have a payment method yet.</div>
        ) : (
          data.methods.map((m) => {
            const total = m.total_bookings || 0;
            const paidPct = total > 0 ? Math.round((m.paid_bookings / total) * 100) : 0;
            return (
              <div key={m.method} className="rounded-2xl border border-gray-200 p-3 sm:p-4 hover:border-emerald-200 transition-colors" data-testid={`payment-method-row-${m.method}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <PaymentMethodChip method={m.method} methodLabel={m.label} size="lg" />
                    <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">{total} bookings</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-700">Paid</div>
                      <div className="font-extrabold text-gray-900 tabular-nums" data-testid={`pm-paid-${m.method}`}>
                        {m.paid_bookings} <span className="text-gray-400 font-normal">·</span> <span className="text-emerald-700">${m.paid_revenue.toFixed(0)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-amber-700">Unpaid</div>
                      <div className="font-extrabold text-gray-900 tabular-nums" data-testid={`pm-unpaid-${m.method}`}>
                        {m.unpaid_bookings} <span className="text-gray-400 font-normal">·</span> <span className="text-amber-700">${m.unpaid_amount.toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Paid % progress bar */}
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${paidPct}%` }} />
                </div>
                <div className="mt-1 text-[10px] text-gray-500 font-bold uppercase tracking-wider">{paidPct}% paid</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl px-3 py-2.5 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{label}</div>
      <div className={`mt-0.5 text-lg sm:text-xl font-extrabold tabular-nums tracking-tight ${accent ? "text-[#047857]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
